import { computed, ref } from 'vue'
import { createInjectionState, toReactive } from '@vueuse/core'
import {
  ToQueryStringFilters,
  recordActions,
  useDate,
  useOrderByFilter,
  usePagination,
  useToQueryString,
} from '@/core'
import type { TPurchaseOrder } from '../../../types'
import { useModuleQuery } from '../query/useModuleQuery'
import {
  Pagination,
  TBaseFileCertificate,
  TImportFile,
  TPayment,
  TRecordAction,
} from '@/core/types'
import { useRoute, useRouter } from 'vue-router'
import { useCatalogStore } from '@/core/store/catalogs'
import { storeToRefs } from 'pinia'
import { usePaymentsQuery } from '../query/usePaymentsQuery'

const [useProvideModule, useModule] = createInjectionState(() => {
  const rows = ref<number[]>([])
  const form = ref<TPurchaseOrder>({})
  const modelId = ref<number>()
  const millFilters = ref<ToQueryStringFilters>({
    status_id: [1],
    order_by: 'created_at,desc',
  })
  const supplierFilters = ref<ToQueryStringFilters>({
    status_id: [1],
    order_by: 'created_at,desc',
  })
  const paymentsFilters = ref<ToQueryStringFilters>({
    order_by: 'created_at,desc',
  })
  const filters = ref<ToQueryStringFilters>({
    dates: {
      created_at: [],
    },
    galvanized_grade_id: [],
    gauge_id: [],
    hardness_id: [],
    search: '',
    order_by: 'created_at,desc',
  })

  const route = useRoute()
  const router = useRouter()
  const store = useCatalogStore()
  const { catalogs } = storeToRefs(store)

  const isHistory = computed(
    () =>
      !!route.query?.action &&
      route.query?.action === recordActions.show_history,
  )
  const isPayment = computed(
    () =>
      !!route.query?.action &&
      route.query?.action === recordActions.show_payments,
  )
  const moduleQueryParams = useToQueryString(toReactive(filters))
  const supplierQueryParams = useToQueryString(toReactive(supplierFilters))
  const millQueryParams = useToQueryString(toReactive(millFilters))
  const paymentQueryParams = useToQueryString(toReactive(paymentsFilters))

  const { zonedTimeToUtc } = useDate()

  const paginationModule = usePagination()
  const paginationPayments = usePagination()

  const moduleQuery = useModuleQuery({
    currentPage: paginationModule.currentPage,
    modelId: computed(() => Number(modelId.value)),
    filters: {
      suppliers: supplierQueryParams.queryParams,
      millsCatalog: millQueryParams.queryParams,
      loadRecords: moduleQueryParams.queryParams,
    },
    enableQuery: {
      loadRecord: computed(
        () =>
          !!modelId.value &&
          !isNaN(Number(modelId.value)) &&
          isPayment.value &&
          !isHistory.value,
      ),
      millsCatalog: true,
      loadHistoryActions: computed(
        () =>
          !!modelId.value && !isNaN(Number(modelId.value)) && isHistory.value,
      ),
    },
    handlers: {
      onDataChange: (pagination: Pagination) => {
        paginationModule.setPagination(pagination)
      },
    },
  })

  const paymentsQuery = usePaymentsQuery({
    modelId: computed(() => Number(modelId.value)),
    currentPage: paginationPayments.currentPage,
    filters: {
      loadRecords: paymentQueryParams.queryParams,
    },
    enableQuery: {
      loadRecords: computed(
        () =>
          !!modelId.value &&
          !isNaN(Number(modelId.value)) &&
          isPayment.value &&
          !isHistory.value,
      ),
    },
    handlers: {
      onDataChange: (pagination) => {
        paginationPayments.setPagination(pagination)
      },
    },
  })

  const { orderByOptions } = useOrderByFilter({
    columns: ['created_at', 'id'],
  })

  const onAdd = async (payload: TPurchaseOrder) => {
    const order = await moduleQuery.addRecord.mutateAsync({
      ...payload,
      entry_date: zonedTimeToUtc(new Date(payload.entry_date ?? ''), {
        removeTimeZone: true,
      }),
      init_date: zonedTimeToUtc(new Date(payload.init_date ?? ''), {
        removeTimeZone: true,
      }),
    })
    onRedirectToEdit(order)
  }

  const onEdit = async (payload: TPurchaseOrder) => {
    await moduleQuery.editRecord.mutateAsync({
      ...payload,
      entry_date: zonedTimeToUtc(new Date(payload.entry_date ?? ''), {
        removeTimeZone: true,
      }),
      init_date: zonedTimeToUtc(new Date(payload.init_date ?? ''), {
        removeTimeZone: true,
      }),
    })
  }

  const onPrint = async (id: number) => {
    await moduleQuery.printRecord(id)
  }

  const onImport = async (data: TImportFile) => {
    await moduleQuery.importRecords.mutateAsync(data)
  }

  const onAction = async (action: TRecordAction) => {
    await moduleQuery.actionRecord.mutateAsync(action)
  }

  const onDelete = async (payload: TPurchaseOrder) => {
    await moduleQuery.deleteRecord.mutateAsync(payload)
  }

  const onUploadCertificate = async (
    payload: TPurchaseOrder & TBaseFileCertificate,
  ) => {
    await moduleQuery.uploadCertificate.mutateAsync(payload)
  }

  const onHistoryActions = (id: number) => {
    modelId.value = id
  }

  const onLoadPayments = (id: number) => {
    modelId.value = id
    paymentsQuery.loadRecords.refetch()
  }

  const onRequestCatalog = async (payload: number[]) => {
    await moduleQuery.requestCatalog.mutateAsync({ ids: payload })
  }

  const onAddPayment = async (payload: TPayment) => {
    await paymentsQuery.addRecord.mutateAsync(payload)
    const order = await moduleQuery.loadRecord.refetch()
    moduleQuery.serviceHandlers.updateRecord(order.data as TPurchaseOrder)
  }

  const onDeletePayment = async (payload: TPayment) => {
    await paymentsQuery.deleteRecord.mutateAsync(payload)
    const order = await moduleQuery.loadRecord.refetch()
    moduleQuery.serviceHandlers.updateRecord(order.data as TPurchaseOrder)
  }

  const onRedirectToEdit = (order: TPurchaseOrder) => {
    router.push({
      name: 'purchaseOrdersDetail',
      params: {
        id: order.id,
      },
    })
  }

  const tableData = computed(() => moduleQuery.loadRecords.data?.value?.data)
  const model = computed(() => moduleQuery.loadRecord?.data?.value)
  const suppliers = computed(() => moduleQuery.suppliersCatalog?.data?.value)
  const history = computed(() => moduleQuery.loadHistoryActions?.data?.value)
  const payments = computed(() => paymentsQuery.loadRecords?.data?.value?.data)
  const mills = computed(() => moduleQuery?.millsCatalog.data?.value)

  const isLoading = computed(
    () =>
      moduleQuery.addRecord.isLoading.value ||
      moduleQuery.editRecord.isLoading.value ||
      moduleQuery.loadRecords.isLoading.value,
  )

  const isLoadingHistory = computed(
    () => moduleQuery.loadHistoryActions?.isLoading.value,
  )

  const isLoadingCategories = computed(
    () => moduleQuery.requestCatalog.isLoading.value,
  )

  const isLoadingMills = computed(
    () => moduleQuery.millsCatalog.isLoading.value,
  )

  const isLoadingPayments = computed(
    () =>
      paymentsQuery.loadRecords.isLoading.value ||
      paymentsQuery.addRecord.isLoading.value ||
      paymentsQuery.deleteRecord.isLoading.value,
  )

  const isLoadingSuppliers = computed(
    () => moduleQuery.suppliersCatalog.isLoading.value,
  )

  return {
    history,
    model,
    suppliers,
    tableData,
    filters,
    orderByOptions,
    rows,
    form,
    paginationModule: toReactive(paginationModule),
    isLoading,
    isLoadingHistory,
    catalogs,
    isLoadingCategories,
    payments,
    isPayment,
    isHistory,
    isLoadingPayments,
    mills,
    isLoadingMills,
    isLoadingSuppliers,
    paginationPayments: toReactive(paginationPayments),
    onDelete,
    onDeletePayment,
    onLoadPayments,
    onHistoryActions,
    onImport,
    onAction,
    onPrint,
    onEdit,
    onAdd,
    onUploadCertificate,
    onRequestCatalog,
    onAddPayment,
  }
})

export { useProvideModule, useModule }
